import { Fragment } from 'react'
import { useProfile } from '../../api/queries'
import { useAuthenticatedSession } from '../../auth/session'
import { loginHref } from '../../bff/client'
import { useI18n } from '../../i18n'
import { Button, Skeleton } from '../../ui'
import styles from './profile.module.css'

/**
 * Password change and authenticator re-enrolment are Keycloak
 * Application-Initiated Actions, reached by a full navigation to
 * `/bff/login?…`. The SPA deliberately owns neither flow: a portal that
 * renders its own password field is a phishing template, and re-implementing
 * TOTP enrolment would duplicate a screen Keycloak already renders correctly
 * with the realm's own policy applied.
 */
const aiaHref = (action: string, returnTo: string, uiLocales: string): string =>
  loginHref({ kcAction: action, returnTo, uiLocales })

export function Profile() {
  const { t, locale } = useI18n()
  const { user } = useAuthenticatedSession()
  const profile = useProfile()

  const details: ReadonlyArray<readonly [string, string]> = [
    [t('profile.name'), profile.data?.full_name ?? user.name],
    [t('profile.email'), profile.data?.email ?? user.email],
    [t('profile.roles'), (profile.data?.roles ?? user.roles).join(', ')],
    [t('profile.organisation'), profile.data?.organisation ?? '—'],
    [t('profile.biller'), profile.data?.biller_id ?? user.billerId ?? t('profile.allBillers')],
  ]

  return (
    <>
      <h1 className={styles.title}>{t('nav.profile')}</h1>

      <div className={styles.grid}>
        <section className={styles.card} aria-labelledby="profile-scope">
          <h2 className={styles.cardTitle} id="profile-scope">
            {t('profile.scopeTitle')}
          </h2>
          {profile.isPending ? (
            <Skeleton count={5} label={t('common.loading')} />
          ) : (
            <>
              <dl className={styles.definitions}>
                {details.map(([term, value]) => (
                  <Fragment key={term}>
                    <dt className={styles.term}>{term}</dt>
                    <dd className={styles.value}>{value}</dd>
                  </Fragment>
                ))}
              </dl>
              {profile.data && profile.data.assigned_gateways.length > 0 && (
                <>
                  <p className={styles.term}>
                    {t('profile.gateways')}
                  </p>
                  <div className={styles.chips}>
                    {profile.data.assigned_gateways.map((g) => (
                      <span key={g.gateway_id} className={styles.chip}>
                        {g.gateway_name}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </section>

        <section className={styles.card} aria-labelledby="profile-security">
          <h2 className={styles.cardTitle} id="profile-security">
            {t('profile.securityTitle')}
          </h2>
          <div className={styles.actions}>
            <Button
              variant="secondary"
              onClick={() => window.location.assign(aiaHref('UPDATE_PASSWORD', '/profile', locale))}
            >
              {t('profile.changePassword')}
            </Button>
            <p className={styles.hint}>{t('profile.changePasswordHint')}</p>
            <Button
              variant="secondary"
              onClick={() => window.location.assign(aiaHref('CONFIGURE_TOTP', '/profile', locale))}
            >
              {t('profile.reconfigureMfa')}
            </Button>
            <p className={styles.hint}>{t('profile.reconfigureMfaHint')}</p>
          </div>
        </section>

        {/*
          The personal access log was here and is deliberately gone.

          PRD FR-37: "Audit logs must be accessible to Maviance Ops and
          Compliance teams but not to biller users." Showing someone their own
          sign-in history is a defensible security feature and common
          practice — but it is not what the requirement says, and this screen
          is itself marked "Version 2 - NOT MVP". Keeping a feature contrary
          to a written requirement because it felt useful is how a compliance
          review turns into an argument.

          The events are still recorded. An internal user reads them via
          GET /v1/audit?scope=biller.
        */}
      </div>
    </>
  )
}
